import { CollapsibleMenu } from "@whatslly/ui-components/CollapsibleMenu";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ChatDateFilter.module.scss";
import classnames from "classnames";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@whatslly/ui-components/v2/BaseElements/DatePicker";
import { Dayjs } from "dayjs";
import { LocalizationProvider as MuiXLocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs as MuiXAdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getSupportedDayjsLocale } from "@whatslly/commons/browserUtils";
import { observer } from "mobx-react-lite";
import { Button } from "@whatslly/ui-components/v2/Button";
import type { DateFilterMonthsOptions } from "@whatsllyorg/crm-components/ChatComponent/types/DateFilterMonthsOptions";
import { FormFieldV2 } from "@whatslly/ui-components/v2/FormElements/FormField";
import dayjs from "dayjs";

const locale = getSupportedDayjsLocale();

const DEFAULT_DATE_FILTER_OPTIONS = ["3", "6", "9", "12"] as const;
const SIDECARS_DATE_FILTER_OPTIONS = ["1", "2", "4", "6"] as const;

const FilterButton: React.FC<{ isOpen: boolean; disabled?: boolean; testId?: string }> = ({ isOpen, disabled, testId }) => (
  <div
    data-testid={testId ?? "filter-messages-date-button"}
    className={classnames(styles.headerButton, {
      [styles.selected]: isOpen,
      [styles.disabled]: disabled,
    })}
  >
    <SvgV2 width={12} height={12} iconName={`calendar`} />
  </div>
);

type ByMonthOnlyType = {
  onChange: (value: DateFilterMonthsOptions) => void;
  onDateRangeChange?: undefined;
  onFilterTypeChange?: undefined;
  dateRange?: undefined;
  openInMode?: undefined;
  hideByMonth?: undefined;
};

type DateRangeAndByMonthEnabledType = {
  onChange: (value: DateFilterMonthsOptions) => void;
  onDateRangeChange: (newRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) => void;
  onFilterTypeChange?: (mode: "byMonth" | "range") => void;
  dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null };
  openInMode?: "byMonth" | "range";
  hideByMonth?: boolean;
};

type ByRangeOnly = {
  onChange?: undefined;
  onDateRangeChange: (newRange: { fromDate: Dayjs | null; toDate: Dayjs | null }) => void;
  onFilterTypeChange?: undefined;
  dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null };
  openInMode?: "byMonth" | "range";
  hideByMonth: true;
};

type ChatDateFilterProps = {
  defaultValue?: DateFilterMonthsOptions;
  disabled?: boolean;
  isSideCarFilter?: boolean;
  openCalendarInTheMiddle?: boolean;
  title?: string;
  limitDateRange?: number;
  testId?: string;
} & (ByMonthOnlyType | DateRangeAndByMonthEnabledType | ByRangeOnly);

export const ChatDateFilter: React.FC<ChatDateFilterProps> = observer(
  ({
    openCalendarInTheMiddle,
    onChange,
    defaultValue,
    disabled,
    onFilterTypeChange,
    onDateRangeChange,
    dateRange,
    openInMode,
    isSideCarFilter,
    hideByMonth,
    title,
    limitDateRange,
    testId,
  }) => {
    const [selected, setSelected] = useState<DateFilterMonthsOptions | undefined>(defaultValue || "1");
    const [selectedFilterMode, setSelectedFilterMode] = useState<"byMonth" | "range">(hideByMonth ? "range" : openInMode || `byMonth`);
    const [hideXMonthSelected, setHideXMonthSelected] = useState(openInMode === "range");
    const menuRef = useRef<{ close: () => void }>(null);
    const { t } = useTranslation();
    const filterOptions = useMemo(() => (isSideCarFilter ? SIDECARS_DATE_FILTER_OPTIONS : DEFAULT_DATE_FILTER_OPTIONS), [isSideCarFilter]);

    const handleOnChange = useCallback(
      (value: DateFilterMonthsOptions) => {
        setSelected(value);
        onChange?.(value);
        setHideXMonthSelected(false);
        menuRef.current?.close();
      },
      [onChange]
    );

    const handleChangeFilterType = useCallback(
      (filterType: "byMonth" | "range") => {
        setSelectedFilterMode(filterType);
        onFilterTypeChange?.(filterType);
      },
      [onFilterTypeChange]
    );

    return (
      <MuiXLocalizationProvider dateAdapter={MuiXAdapterDayjs} adapterLocale={locale}>
        <CollapsibleMenu
          title={(title || t("chat_component.messages_created_date") || "").toUpperCase()}
          button={({ isOpen }) => <FilterButton isOpen={isOpen} disabled={disabled} testId={testId} />}
          menuClassName={styles.collapseMenu}
          ref={menuRef}
          disabled={disabled}
          ignoreCloseOnClickOutside={selectedFilterMode === "range"}
          className={styles.collapsibleMenu}
        >
          {onDateRangeChange && (
            <>
              <div
                className={classnames(styles.dateRangeContainer, {
                  [styles.active]: selectedFilterMode === "range",
                  [styles.byMonthIsHidden]: hideByMonth,
                })}
              >
                {!hideByMonth && (
                  <div className={styles.optionWrapper}>
                    <div
                      className={classnames(styles.optionContainer, styles.filterType, { [styles.selected]: selectedFilterMode === "range" })}
                      onClick={() => selectedFilterMode !== "range" && handleChangeFilterType("range")}
                      data-testid={"date-filter-range"}
                    >
                      {t("chat_component.by_date_range")}
                    </div>
                  </div>
                )}
                <DateRangeComponent
                  openCalendarInTheMiddle={openCalendarInTheMiddle}
                  onChange={(dates) => {
                    setHideXMonthSelected(true);
                    onDateRangeChange(dates);
                    menuRef.current?.close();
                  }}
                  dates={dateRange}
                  limitDateRange={limitDateRange}
                />
              </div>
              {!hideByMonth && <div className={styles.dateRangeAndByMonthSeparator}></div>}
            </>
          )}
          {!hideByMonth && (
            <div className={classnames(styles.byLastMonthsContainer, { [styles.active]: selectedFilterMode === "byMonth" })}>
              {onDateRangeChange && (
                <div className={styles.optionWrapper}>
                  <div
                    className={classnames(styles.optionContainer, styles.filterType, {
                      [styles.selected]: selectedFilterMode === "byMonth",
                    })}
                    onClick={() => selectedFilterMode !== "byMonth" && handleChangeFilterType("byMonth")}
                    data-testid={"date-filter-by-month"}
                  >
                    {t("chat_component.by_last_months")}
                  </div>
                </div>
              )}

              <div className={styles.byLastMonthsValuesContainer}>
                {filterOptions.map((value) => (
                  <div key={value} className={styles.optionWrapper} data-testid={`date-filter-option`}>
                    <div
                      className={classnames(styles.optionContainer, styles.option, { [styles.selected]: selected === value && !hideXMonthSelected })}
                      onClick={() => handleOnChange(value)}
                    >
                      {t("chat_component.last_months", { months: value })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleMenu>
      </MuiXLocalizationProvider>
    );
  }
);

const INPUTS_TO_RENDER_ARRAY = ["fromDate", "toDate"] as const;

const DateRangeComponent: React.FC<{
  onChange: (dates: { fromDate: Dayjs | null; toDate: Dayjs | null }) => void;
  dates: { fromDate: Dayjs | null; toDate: Dayjs | null };
  openCalendarInTheMiddle?: boolean;
  limitDateRange?: number;
}> = ({ onChange, dates, openCalendarInTheMiddle, limitDateRange }) => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(dates.fromDate);
  const [toDate, setToDate] = useState<Dayjs | null>(dates.toDate);
  const [errors, setErrors] = useState<Record<"toDateError" | "fromDateError", string | null>>({
    toDateError: null,
    fromDateError: null,
  });
  const { t } = useTranslation();

  const dateLimit = useMemo(
    () =>
      dayjs()
        .subtract(limitDateRange || 0, "months")
        .startOf("day"),
    [limitDateRange]
  );
  const today = useMemo(() => dayjs().endOf("day"), []);

  const setError = useCallback((field: "fromDateError" | "toDateError", error: string | null) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
      [field === "fromDateError" ? "toDateError" : "fromDateError"]: null,
    }));
  }, []);

  const isValidInput = useMemo(
    () =>
      fromDate?.isValid() &&
      toDate?.isValid() &&
      (fromDate.isSame(toDate) || fromDate.isBefore(toDate)) &&
      !errors.toDateError &&
      !errors.fromDateError,
    [errors.fromDateError, errors.toDateError, fromDate, toDate]
  );

  const fireOnChange = useCallback(() => isValidInput && onChange({ fromDate, toDate }), [fromDate, isValidInput, onChange, toDate]);

  const validateDateRange = useCallback(
    (date: Dayjs | null, otherDate: Dayjs | null, field: "fromDateError" | "toDateError") => {
      if (date) {
        if (limitDateRange && date.isBefore(dateLimit)) {
          setError(field, t("chat_component.date_cannot_be_older_than_x_months", { months: limitDateRange }));
          return false;
        }

        if (date.isAfter(today)) {
          setError(field, t("chat_component.date_cannot_be_in_future"));
          return false;
        }

        if (otherDate) {
          if (field === "fromDateError" && date.isAfter(otherDate)) {
            setError(field, t("chat_component.from_date_cannot_be_bigger_then_to_date"));
            return false;
          }

          if (field === "toDateError" && date.isBefore(otherDate)) {
            setError(field, t("chat_component.to_date_cannot_be_smaller_then_from_date"));
            return false;
          }
        }
      }

      setError(field, null);
      return true;
    },
    [dateLimit, limitDateRange, setError, t, today]
  );

  const handleDateChange = useCallback(
    (field: "fromDate" | "toDate", date: Dayjs | null) => {
      const setDate = field === "fromDate" ? setFromDate : setToDate;
      const otherDate = field === "fromDate" ? toDate : fromDate;
      const errorField = field === "fromDate" ? "fromDateError" : "toDateError";

      if (
        (field === "fromDate" && fromDate?.startOf("day").isSame(date?.startOf("day"))) ||
        (field === "toDate" && toDate?.startOf("day").isSame(date?.startOf("day")))
      ) {
        return;
      }

      setDate(date);
      validateDateRange(date, otherDate, errorField);
    },
    [fromDate, toDate, validateDateRange]
  );

  useEffect(() => {
    if (limitDateRange) {
      if (fromDate && (fromDate.isBefore(dateLimit) || fromDate.isAfter(today))) {
        setFromDate(null);
        setError("fromDateError", t("chat_component.date_cannot_be_older_than_x_months", { months: limitDateRange }));
      }
      if (toDate && (toDate.isBefore(dateLimit) || toDate.isAfter(today))) {
        setToDate(null);
        setError("toDateError", t("chat_component.date_cannot_be_older_than_x_months", { months: limitDateRange }));
      }
    }
  }, [fromDate, limitDateRange, dateLimit, setError, t, toDate, today]);

  return (
    <div className={classnames(styles.fromToDateWrapper)}>
      {INPUTS_TO_RENDER_ARRAY.map((field) => (
        <div key={field} className={styles[`${field}Container`]}>
          <label htmlFor="">{t(`chat_component.${field === "fromDate" ? "from" : "to"}`)}</label>
          <FormFieldV2 showError={Boolean(errors[`${field}Error`])} errorText={errors[`${field}Error`] || ""}>
            <DatePicker
              data-testid={`date-filter-range-${field === "fromDate" ? "from" : "to"}`}
              onChange={(date) => handleDateChange(field as "fromDate" | "toDate", date)}
              darkMode={false}
              value={field === "fromDate" ? fromDate : toDate}
              openCalendarInTheMiddle={openCalendarInTheMiddle}
              maxDate={limitDateRange ? today.toDate() : undefined}
              minDate={limitDateRange ? dateLimit.toDate() : undefined}
            />
          </FormFieldV2>
        </div>
      ))}
      {isValidInput && (
        <div className={styles.applyDateRangeButtonContainer}>
          <Button disabled={!isValidInput} text={t("chat_component.apply")} onClick={fireOnChange} testId="date-filter-apply-range" />
        </div>
      )}
    </div>
  );
};
